import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FiMoreHorizontal, FiX } from "react-icons/fi";
import Brand from "./Brand";
import BusinessSwitcher from "./BusinessSwitcher";
import UserMenu from "./UserMenu";
import SafeIcon from "../common/SafeIcon";
import { accountantNavigationGroups, navigation, navigationGroups, resolveOwnerNavigationGroups } from "../data/navigation";
import {useBusiness} from '../contexts/BusinessContext';
import {preloadRoute} from '../routes/lazyPages';
import {usePlanAccess} from '../hooks/usePlanAccess';
import {useLanguage} from '../contexts/LanguageContext';
import {getBlogEditor} from '../lib/blog/blogService';
import {FiEdit3} from 'react-icons/fi';
import {useSoftwareKeyboardVisible} from '../hooks/useSoftwareKeyboardVisible';
export default function MobileNavigation({ session }) {
  const {activeAccess}=useBusiness();
  const {isMileagePlan,isPlanResolved}=usePlanAccess();
  const {t}=useLanguage();
  const [open, setOpen] = useState(false);
  const [isBlogEditor,setIsBlogEditor]=useState(false);
  const softwareKeyboardVisible=useSoftwareKeyboardVisible();
  const dialog = useRef(null),
    trigger = useRef(null);
  const location = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (open) dialog.current?.showModal();
    else if (dialog.current?.open) {
      dialog.current.close();
      trigger.current?.focus();
    }
  }, [open]);
  const accountantMode=activeAccess?.mode==='accountant';
  const ownerGroups=resolveOwnerNavigationGroups(navigationGroups,{isMileagePlan,isPlanResolved});
  const groups=accountantMode ? accountantNavigationGroups : (isBlogEditor?[...ownerGroups,{label:'Publishing',items:[{label:'Blog studio',path:'/settings/blog',icon:FiEdit3}]}]:ownerGroups);
  const restrictedOwnerNavigation=!isPlanResolved||isMileagePlan;
  const core = accountantMode
    ? accountantNavigationGroups[0].items
    : navigation.filter((x) => (restrictedOwnerNavigation
      ? ["/", "/trips", "/vehicles", "/settings"]
      : ["/", "/expenses", "/trips", "/review"]).includes(x.path));
  return (
    <>
      <header className="sticky top-0 z-30 flex min-h-20 items-center gap-3 border-b border-slate-200/70 bg-white pb-3 pl-[calc(1rem+var(--app-safe-area-left))] pr-[calc(1rem+var(--app-safe-area-right))] pt-[calc(0.75rem+var(--app-safe-area-top))] lg:hidden">
        <Brand compact />
        <div className="min-w-0 flex-1">
          <BusinessSwitcher compact />
        </div>
      </header>
      <nav
        aria-label={t('Mobile navigation')}
        className={`fixed inset-x-0 bottom-0 z-40 ${softwareKeyboardVisible?'hidden':'grid'} ${accountantMode?'grid-cols-4':'grid-cols-5'} border-t border-slate-200 bg-white px-2 pb-[var(--app-safe-area-bottom)] shadow-[0_-4px_24px_rgba(19,44,59,0.04)] lg:hidden`}
      >
        {core.slice(0,4).map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === "/"}
            onTouchStart={() => preloadRoute(item.path)}
            onFocus={() => preloadRoute(item.path)}
            className={({ isActive }) =>
              `my-2 flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl text-[11px] font-semibold ${isActive ? "bg-mint text-primary" : "text-slate-500"}`
            }
          >
            <SafeIcon icon={item.icon} className="h-5 w-5" />
            {t(item.label === 'Dashboard' ? 'Home' : item.label)}
          </NavLink>
        ))}
        <button
          ref={trigger}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen(true)}
          className="my-2 flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl text-[11px] font-semibold text-slate-600"
        >
          <FiMoreHorizontal className="h-5 w-5" />
          {t('More')}
        </button>
      </nav>
      <dialog
        aria-labelledby="workspace-menu-title"
        ref={dialog}
        onCancel={() => setOpen(false)}
        onClick={(event) => {
          if (event.target === dialog.current) setOpen(false);
        }}
        className="m-auto w-[calc(100%_-_2rem)] max-w-md rounded-xl border border-slate-200 bg-white p-0 text-ink shadow-2xl backdrop:bg-ink/40"
      >
        <div className="flex items-center justify-between border-b border-slate-100 p-5">
          <h2 id="workspace-menu-title" className="text-lg font-semibold">
            {t('Your workspace')}
          </h2>
          <button
            aria-label={t('Close menu')}
            className="grid h-11 w-11 place-items-center rounded-xl bg-surface"
            onClick={() => setOpen(false)}
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>
        <nav
          aria-label={t('All pages')}
          className="max-h-[60dvh] space-y-4 overflow-y-auto p-4"
        >
          {groups.map((group) => (
            <div key={group.label}>
              <p className="mb-2 px-2 text-xs font-semibold text-slate-500">
                {t(group.label)}
              </p>
              <div className="grid grid-cols-1 gap-1 min-[360px]:grid-cols-2">
                {group.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === "/"}
                    onClick={() => setOpen(false)}
                    onTouchStart={() => preloadRoute(item.path)}
                    onFocus={() => preloadRoute(item.path)}
                    className={({ isActive }) =>
                      `flex min-h-14 items-center gap-3 rounded-xl p-3 text-sm font-medium ${isActive ? "bg-mint text-primary" : "hover:bg-surface"}`
                    }
                  >
                    <SafeIcon icon={item.icon} className="h-5 w-5 shrink-0" />
                    {t(item.label)}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t border-slate-100 p-5">
          <UserMenu session={session} />
        </div>
      </dialog>
    </>
  );
}
